import { Color, Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { UseFieldArrayAppend, UseFieldArrayMove, UseFieldArrayRemove } from 'react-hook-form';
import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { GraphNodePropertyRow } from 'dashboard/components/FormControls/GraphNodeConfigurationRow/GraphNodePropertyRow';
import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import { GraphAttributes, GraphProperty } from 'dashboard/models/graph';
import { Heading } from 'design-system/components/Text/Text';

interface GraphColoringRulesPropertyProps {
  nodeProperties: GraphProperty[];
  move: UseFieldArrayMove;
  remove: UseFieldArrayRemove;
  append: UseFieldArrayAppend<GraphAttributes> | UseFieldArrayAppend<GraphNodeConfigFormData>;
  defaultGraphProperties: GraphProperty[];
  onGraphConfigEdit: () => void;
  isCustomNode: boolean;
}

const PropertiesList = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : Color.CoreSlate50};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const PropertiesContainer = styled.div`
  background-color: ${Color.CoreSlate50};
  padding: ${Spacing[2]};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

export const GraphNodeProperty = ({
  nodeProperties,
  move,
  remove,
  append,
  defaultGraphProperties,
  onGraphConfigEdit,
  isCustomNode,
}: GraphColoringRulesPropertyProps) => {
  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || result.destination.index >= nodeProperties.length) {
      return;
    }
    move(result.source.index, result.destination.index);
    onGraphConfigEdit();
  };

  const nodePropertiesValues = useMemo(
    () => nodeProperties.map((property) => property.value),
    [nodeProperties],
  );

  const renderedGraphProperties = useMemo(
    () =>
      isCustomNode
        ? nodeProperties
        : [
            ...nodeProperties,
            ...defaultGraphProperties.filter(
              (properties) => !nodePropertiesValues.includes(properties.value),
            ),
          ],
    [defaultGraphProperties, nodeProperties, nodePropertiesValues, isCustomNode],
  );

  const renderPropertiesProperties = (
    droppableProvided: DroppableProvided,
    droppableSnapshot: DroppableStateSnapshot,
  ) => (
    <PropertiesList
      ref={droppableProvided.innerRef}
      isDraggingOver={droppableSnapshot.isDraggingOver}
    >
      {renderedGraphProperties.map((graphProperty, index) => {
        return (
          <GraphNodePropertyRow
            key={index}
            graphProperty={graphProperty}
            index={index}
            dimmed={!nodePropertiesValues.includes(graphProperty.value)}
            remove={remove}
            append={append}
            onGraphConfigEdit={onGraphConfigEdit}
            isCustomNode={isCustomNode}
          />
        );
      })}
      {droppableProvided.placeholder}
    </PropertiesList>
  );

  return (
    <>
      <Heading size='sm'>Hover Card Properties</Heading>
      <PropertiesContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='droppable'>{renderPropertiesProperties}</Droppable>
        </DragDropContext>
      </PropertiesContainer>
    </>
  );
};

export default GraphNodeProperty;
